import React, { useMemo, useState } from "react";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  PhoneOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import { Alert, Button, Card, DatePicker, Space, Table, Tag, Typography, message } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { SalesMeetingSaleStatus, SalesMeetingStatus } from "@fitness-app/data-models/entities/SalesMeeting";

import { usePermissionRequirement } from "~/hooks/usePermission";
import { dashboardPermissionRequirements } from "~/modules/Dashboard/dashboardPermissionRequirements";
import { salesMeetingBundleSelectFields, type SalesMeetingRow } from "~/modules/Sales/hooks/useSalesMeetingsQuery";
import { getConfirmedSaleBundle, getMeetingBundle, isWonMeeting } from "~/modules/Sales/salesCrmUtils";
import { supabase, useAppSelector } from "~/store/initializeStore";

const { RangePicker } = DatePicker;

const statusColor: Record<string, string> = {
  [SalesMeetingStatus.Scheduled]: "blue",
  [SalesMeetingStatus.Attended]: "green",
  [SalesMeetingStatus.NoShow]: "red",
  [SalesMeetingStatus.Canceled]: "default",
  [SalesMeetingStatus.Rescheduled]: "orange",
};

const meetingStatusLabels: Record<SalesMeetingStatus, string> = {
  [SalesMeetingStatus.Scheduled]: "Zaplanowane",
  [SalesMeetingStatus.Attended]: "Odbyte",
  [SalesMeetingStatus.NoShow]: "Nie pojawiła się",
  [SalesMeetingStatus.Canceled]: "Odwołane",
  [SalesMeetingStatus.Rescheduled]: "Przełożone",
};

const saleStatusLabels: Record<SalesMeetingSaleStatus, string> = {
  [SalesMeetingSaleStatus.Yes]: "Tak",
  [SalesMeetingSaleStatus.No]: "Nie",
  [SalesMeetingSaleStatus.Waiting]: "Czekam",
  [SalesMeetingSaleStatus.NotQualified]: "Nie kwalifikuje się",
};

const normalizeCurrency = (currency: string | null | undefined) => (currency || "PLN").toUpperCase();

const formatMoney = (value: number | null | undefined, currency = "PLN") =>
  typeof value === "number"
    ? new Intl.NumberFormat("pl-PL", {
        style: "currency",
        currency: normalizeCurrency(currency),
        minimumFractionDigits: 2,
      }).format(value / 100)
    : "-";

const getRangePresets = (): { label: string; value: [Dayjs, Dayjs] }[] => [
  { label: "Dziś", value: [dayjs(), dayjs()] },
  { label: "Ten tydzień", value: [dayjs().startOf("week"), dayjs()] },
  { label: "Ten miesiąc", value: [dayjs().startOf("month"), dayjs()] },
  {
    label: "Poprzedni miesiąc",
    value: [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")],
  },
  { label: "Ostatnie 30 dni", value: [dayjs().subtract(30, "day"), dayjs()] },
];

const RepMetricCard = ({
  title,
  value,
  helper,
  icon,
  accent,
  loading,
}: {
  title: string;
  value: string | number;
  helper: string;
  icon: React.ReactNode;
  accent: string;
  loading?: boolean;
}): React.ReactElement => (
  <Card
    loading={loading}
    styles={{ body: { padding: 20 } }}
    className="h-full rounded-lg border border-gray-200 shadow-sm"
  >
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <Typography.Text className="text-xs font-medium uppercase tracking-wide text-gray-500">{title}</Typography.Text>
        <div className="mt-3 truncate text-3xl font-semibold tracking-normal text-gray-950">{value}</div>
      </div>
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${accent}`}>{icon}</div>
    </div>
    <Typography.Text className="mt-4 block text-sm text-gray-500">{helper}</Typography.Text>
  </Card>
);

export const SalesRepDashboard = (): React.ReactElement => {
  const queryClient = useQueryClient();
  const selectedMember = useAppSelector((state) => state.team.selectedMember);
  const canManageCheckout = usePermissionRequirement(dashboardPermissionRequirements.salesCheckout);
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf("week"), dayjs().endOf("week")]);

  const meetingsQuery = useQuery({
    queryKey: ["salesRepDashboardMeetings", selectedMember?.id, range[0].toISOString(), range[1].toISOString()],
    enabled: Boolean(selectedMember?.id),
    keepPreviousData: true,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sales_meeting")
        .select(
          `*, customerBundle:customerBundleId(${salesMeetingBundleSelectFields}), matchedCustomerBundle:matchedCustomerBundleId(${salesMeetingBundleSelectFields})`,
        )
        .eq("teamMemberId", selectedMember?.id ?? "")
        .gte("startTime", range[0].startOf("day").toISOString())
        .lte("startTime", range[1].endOf("day").toISOString())
        .order("startTime", { ascending: true });

      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as SalesMeetingRow[];
    },
  });

  const markAttendedMutation = useMutation({
    mutationFn: async (meetingId: string) => {
      const { error } = await supabase
        .from("sales_meeting")
        .update({
          status: SalesMeetingStatus.Attended,
          statusLocked: true,
          updatedAt: new Date().toISOString(),
        } as never)
        .eq("id", meetingId);

      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["salesRepDashboardMeetings"] });
      await queryClient.invalidateQueries({ queryKey: ["salesCrmMeetings"] });
      void message.success("Spotkanie oznaczone jako odbyte");
    },
  });

  const meetings = meetingsQuery.data ?? [];
  const meetingsInitialLoading = meetingsQuery.isInitialLoading;
  const todayKey = dayjs().format("YYYY-MM-DD");

  const kpi = useMemo(() => {
    const activeMeetings = meetings.filter((meeting) => meeting.status !== SalesMeetingStatus.Rescheduled);
    const today = activeMeetings.filter((meeting) => dayjs(meeting.startTime).format("YYYY-MM-DD") === todayKey).length;
    const attended = activeMeetings.filter((meeting) => meeting.status === SalesMeetingStatus.Attended).length;
    const noShow = activeMeetings.filter((meeting) => meeting.status === SalesMeetingStatus.NoShow).length;
    const sales = activeMeetings.filter(isWonMeeting).length;
    const paid = activeMeetings.reduce((sum, meeting) => sum + (getConfirmedSaleBundle(meeting)?.amount ?? 0), 0);

    return {
      today,
      total: activeMeetings.length,
      attended,
      noShow,
      sales,
      paid,
      attendanceRate: activeMeetings.length ? Math.round((attended / activeMeetings.length) * 100) : 0,
      closeRate: attended ? Math.round((sales / attended) * 100) : 0,
    };
  }, [meetings, todayKey]);

  return (
    <Space direction="vertical" size={20} style={{ width: "100%" }}>
      {meetingsQuery.isError ? (
        <Alert
          showIcon
          type="warning"
          message="Nie można wczytać spotkań sprzedażowych"
          description={(meetingsQuery.error as Error).message}
        />
      ) : null}
      <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white px-5 py-4 shadow-sm xl:flex-row xl:items-center xl:justify-between">
        <div className="min-w-0">
          <Typography.Text className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Dashboard sprzedawcy
          </Typography.Text>
          <Typography.Title level={3} className="!mb-0 !mt-1 !text-2xl !font-semibold !tracking-normal">
            {selectedMember ? `${selectedMember.firstName} ${selectedMember.lastName}` : "Moje spotkania"}
          </Typography.Title>
          <Typography.Text className="text-gray-500">
            Spotkania, obecność i wynik sprzedaży w wybranym okresie.
          </Typography.Text>
        </div>
        <Space wrap align="center">
          <RangePicker
            value={range}
            presets={getRangePresets()}
            onChange={(value) => value?.[0] && value?.[1] && setRange([value[0], value[1]])}
          />
          <Link to="/sales">
            <Button>Sales CRM</Button>
          </Link>
          {canManageCheckout ? (
            <Link to="/sales/checkout-variants">
              <Button icon={<ShoppingCartOutlined />}>Warianty koszyka</Button>
            </Link>
          ) : null}
        </Space>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <RepMetricCard
          loading={meetingsInitialLoading}
          title="Dzisiaj"
          value={kpi.today}
          helper={`${kpi.total} spotkań w zakresie`}
          icon={<CalendarOutlined className="text-base text-blue-700" />}
          accent="bg-blue-50"
        />
        <RepMetricCard
          loading={meetingsInitialLoading}
          title="Odbyte"
          value={`${kpi.attended} / ${kpi.attendanceRate}%`}
          helper={`${kpi.noShow} nieobecne`}
          icon={<CheckCircleOutlined className="text-base text-emerald-700" />}
          accent="bg-emerald-50"
        />
        <RepMetricCard
          loading={meetingsInitialLoading}
          title="Sprzedaże"
          value={`${kpi.sales} / ${kpi.closeRate}%`}
          helper="Program Hollywood, konwersja z odbytych rozmów"
          icon={<PhoneOutlined className="text-base text-violet-700" />}
          accent="bg-violet-50"
        />
        <RepMetricCard
          loading={meetingsInitialLoading}
          title="Płatność"
          value={formatMoney(kpi.paid)}
          helper="Pierwsza opłacona kwota z koszyka"
          icon={<DollarOutlined className="text-base text-amber-700" />}
          accent="bg-amber-50"
        />
      </div>

      <Card
        loading={meetingsInitialLoading}
        styles={{ body: { padding: 20 } }}
        className="rounded-lg border border-gray-200 shadow-sm"
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <Typography.Text className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Moje spotkania
            </Typography.Text>
            <Typography.Title level={4} className="!mb-0 !mt-1 !text-lg !font-semibold">
              Lista rozmów sprzedażowych
            </Typography.Title>
          </div>
        </div>
        <Table<SalesMeetingRow> rowKey="id" dataSource={meetings} loading={meetingsInitialLoading} scroll={{ x: true }}>
          <Table.Column<SalesMeetingRow>
            title="Data"
            dataIndex="startTime"
            render={(value) => dayjs(value).format("DD.MM.YYYY HH:mm")}
          />
          <Table.Column<SalesMeetingRow>
            title="Klient"
            render={(_, row) => (
              <Space direction="vertical" size={0}>
                <Typography.Text>{row.clientName ?? "-"}</Typography.Text>
                <Typography.Text type="secondary">{row.clientEmail}</Typography.Text>
              </Space>
            )}
          />
          <Table.Column<SalesMeetingRow>
            title="Status"
            dataIndex="status"
            render={(value: SalesMeetingStatus) => (
              <Tag color={statusColor[value] ?? "default"}>{meetingStatusLabels[value]}</Tag>
            )}
          />
          <Table.Column<SalesMeetingRow>
            title="Status sprzedaży"
            dataIndex="saleStatus"
            render={(value) =>
              value ? (
                <Tag color={value === SalesMeetingSaleStatus.Yes ? "green" : "default"}>
                  {saleStatusLabels[value as SalesMeetingSaleStatus]}
                </Tag>
              ) : (
                "-"
              )
            }
          />
          <Table.Column<SalesMeetingRow>
            title="Płatność w koszyku"
            render={(_, row) => getMeetingBundle(row)?.name ?? "-"}
          />
          <Table.Column<SalesMeetingRow>
            title="Opłacone"
            render={(_, row) => formatMoney(getConfirmedSaleBundle(row)?.amount, getConfirmedSaleBundle(row)?.currency)}
          />
          <Table.Column<SalesMeetingRow>
            title="Zakontraktowane"
            render={(_, row) =>
              formatMoney(getConfirmedSaleBundle(row)?.totalAmount, getConfirmedSaleBundle(row)?.currency)
            }
          />
          <Table.Column<SalesMeetingRow>
            title=""
            fixed="right"
            render={(_, row) => {
              const disabled = [
                SalesMeetingStatus.Attended,
                SalesMeetingStatus.Canceled,
                SalesMeetingStatus.NoShow,
                SalesMeetingStatus.Rescheduled,
              ].includes(row.status);

              return (
                <Button
                  disabled={disabled}
                  loading={markAttendedMutation.isLoading && markAttendedMutation.variables === row.id}
                  onClick={() => markAttendedMutation.mutate(row.id)}
                >
                  Oznacz jako odbyte
                </Button>
              );
            }}
          />
        </Table>
      </Card>
    </Space>
  );
};
